/**
 * NOTE: For compatibility across platforms all calls **must** return a
 * `Promise` object!
 */

import * as Platform from './Platform';
import { PlatformError } from './Errors';
import { Permission } from './Permissions';
import { Log } from '../AuxillaryService/Log';
import asmCrypto from 'asmcrypto-lite';
import { stringToBoolean } from '../Utility';
import { DexieProofOfPlayLogForwarder } from './DexieProofOfPlayLogForwarder';
import { supportsVideoAssetCache } from './interfaces/VideoAssetCacheInterface';

/**
 * @constructor
 * @abstract
 */
function PlayerAbstract() {
    if (this.constructor === PlayerAbstract) {
        throw new Error("Can't instantiate abstract `PlayerAbstract` class!");
    }

    this._permissions = [];
}

/**
 * @type {string}
 */
PlayerAbstract.prototype.name = 'Unknown';

/**
 * @type {number}
 * @deprecated Use 'Feature Detection' instead
 */
PlayerAbstract.prototype.type = Platform.UNKNOWN;

/**
 * List of allowed behaviors.
 * @type {Array.<Permission>}
 * @private
 */
PlayerAbstract.prototype._permissions = undefined;

/**
 * Code to initialize the platform
 * @return {Promise<void>}
 */
PlayerAbstract.prototype.initializePlatform = async function () {
    this._autoPopulatePermissions();
    const allowReachVideo = stringToBoolean(await this.getNamedValue('allow_reach_video', 'no'));
    if (allowReachVideo) {
        this.addPermission(Permission.PLAY_REACH_VIDEO);
    }
};

/**
 * **Feature:** Get the host system's platform type (ie. 'Win32', 'Linux')
 * @return {Promise<string>|Promise<Error>}
 * @todo using `navigator.platform` may not be accurate,
 *       find a better way to get the host system's platform
 */
PlayerAbstract.prototype.getHostPlatform = function () {
    return Promise.resolve(navigator.platform);
};

/**
 * **Feature:** Get Player Version
 * @return {PromiseLike<string>}
 */
PlayerAbstract.prototype.getPlayerVersion = function () {
    return Promise.resolve('unsupported');
};

/**
 * Checks if a player has permission to do a thing.
 * @param {Permission} requested_permission
 * @returns {boolean}
 */
PlayerAbstract.prototype.hasPermission = function (requested_permission) {
    return this._permissions.indexOf(requested_permission) !== -1;
};

/**
 * Give the player permission to do a thing.
 * @param {Permission} new_permission
 */
PlayerAbstract.prototype.addPermission = function (new_permission) {
    if (!this.hasPermission(new_permission)) {
        this._permissions.push(new_permission);
    }
};

/**
 * Revoke the permission the player has to do a thing.
 * @param {Permission} revoked_permission
 */
PlayerAbstract.prototype.removePermission = function (revoked_permission) {
    this._permissions = this._permissions.filter(function (permission) {
        return permission !== revoked_permission;
    });
};

/**
 * List of all the permissions this player has.
 * @returns {Array.<Permission>}
 */
PlayerAbstract.prototype.getListOfPermissions = function () {
    return [].concat(this._permissions);
};

/**
 * Checks if a feature is implemented for this platform
 * @param {string} featureFunctionName
 * @return {boolean}
 * @deprecated use feature detection instead
 */
PlayerAbstract.prototype.implementsFeature = function (featureFunctionName) {
    console.warn(
        '`PlayerAbstract.implementsFeature()` is deprecated, use feature detection instead'
    );
    return typeof this[featureFunctionName] !== 'undefined';
};

/**
 * Check if platform is a specific 'Platform Type'
 * @param {number} platformType - Platform Type (See {@link Platform})
 * @return {boolean}
 * @deprecated use 'Feature Detection' instead
 */
PlayerAbstract.prototype.isType = function (platformType) {
    console.warn('`PlayerAbstract.isType()` is deprecated, use feature detection instead');
    return this.type === platformType;
};

/**
 * Determine minimum player version requirement
 *
 * Returns True if the player's version is greater than or equal to the version
 * number represented by the passed string (e.g. "4.0.13").
 * @param {string} minVersion
 * @returns {Promise.<boolean>}
 * @throws {PlatformError} when the player's version number is not in
 * the proper format
 */
PlayerAbstract.prototype.isAtLeastVersion = function (minVersion) {
    return this.getPlayerVersion().then(function (currentVersion) {
        var versionRegex = /^[0-9]{1,4}(?:\.[0-9]{1,4}){0,3}/;
        var hasValidVersion =
            currentVersion !== 'unsupported' && currentVersion.match(versionRegex);

        if (!hasValidVersion) {
            throw new PlatformError('Could not determine player version.');
        }

        if (Platform.versionCompare(currentVersion, minVersion) >= 0) {
            return Promise.resolve(true);
        } else {
            return Promise.resolve(false);
        }
    });
};

/**
 * Log warning message
 * @param {string} logMessage
 * @protected
 * @deprecated
 */
PlayerAbstract.prototype._logWarning = function (logMessage) {
    var platformObject = { platformName: this.name, platformType: this.type };

    if (Log && Log.Warning) {
        Log.Warning(logMessage, null, __fileline, platformObject);
    } else {
        console.warn(logMessage, platformObject);
    }
};

/**
 * Automatically add permissions for the player based on its configuration.
 * @private
 */
PlayerAbstract.prototype._autoPopulatePermissions = function () {
    if (supportsVideoAssetCache(this)) {
        // Assume the platform is allowed to play REACH Videos if it's able
        // to cache video assets.
        this.addPermission(Permission.PLAY_REACH_VIDEO);
    }
};

/**
 * Returns a unique "hardware" identifier for the player device. This method
 * makes a best effort to return a stable ID for a specific device.
 *
 * Some platforms do not have access to meaningful hardware factors or
 * time-stable uniqueness signals in sufficient quantity and quality so as to
 * produce reasonably unique hardware identifiers. On these platforms (e.g.
 * BrowserPlayer), a random uuid will be generated upon first request instead
 * which has absolutely no hardware specific factors from which it is derived.
 *
 * Once generated, the identifier is persisted to NamedValueCache. As such, the
 * hardware identifier will change only if it is purged from NamedValueCache
 * *and* the hardware factors used to generate the ID have changed.
 *
 * The specific factors used to make the identifier will be platform dependent.
 * Some examples include:
 *
 * - ReachPlayer: the hardware identifier will be a hash of all the network
 *   adapter MAC addresses visible on the system the first time getHardwareId is
 *   called.
 * - BrightSignPlayer: the hardware identifier will be a hash of the device's
 *   serial number.
 *
 * @returns {Promise.<string>}
 * @throws {Error}
 */
PlayerAbstract.prototype.getHardwareId = function () {
    var hardwareIdKey = 'DEVICE_HARDWARE_ID';
    var effectiveHardwareId = null;
    var newHardwareId = null;

    //I am deliberately calling _generateAndGetHardwareId before checking if it
    //exists in named value cache. This is so that getHardwareId rejects/throws
    //if _generateAndGetHardwareId is not implemented in the concrete platform
    //class. This prevents devs from creating a platform without this function
    //implemented.
    return this._generateAndGetHardwareId()
        .then(
            function (hardwareId) {
                newHardwareId = hardwareId;
                return this.getNamedValue(hardwareIdKey).catch(function (reason) {
                    //if named value cache is busted, so be it. we'll
                    //re-generate the ID then
                    Log.Error(
                        'failed to retrieve hardware id from named value cache; reason=' +
                            (reason.message || reason),
                        null,
                        __fileline,
                        reason
                    );
                });
            }.bind(this)
        )

        .then(
            function (savedHardwareId) {
                if (savedHardwareId) {
                    effectiveHardwareId = savedHardwareId;
                    return undefined;
                } else {
                    //there was no saved hardware id, save newHardwareId
                    newHardwareId = asmCrypto.SHA256.hex(newHardwareId);
                    effectiveHardwareId = newHardwareId;

                    return this.setNamedValue(hardwareIdKey, newHardwareId).catch(function (
                        reason
                    ) {
                        //if named value cache is busted, so be it. I guess we
                        //can't stash the ID
                        Log.Error(
                            'failed to save hardware id to named value cache; reason=' +
                                (reason.message || reason),
                            null,
                            __fileline,
                            reason
                        );
                    });
                }
            }.bind(this)
        )
        .then(function () {
            return effectiveHardwareId;
        });
};

/**
 * Generates a unique ID for the device the signage is running on. Deriving
 * classes *MUST* implement this method.
 *
 * It is highly advised that concrete implementations return a hardware
 * identifier that is prefixed in such a way so as to prevent identifier
 * collisions with other platforms. For example, the
 * ReachPlayerHardwareIdGeneratorMixin returns IDs like this:
 *
 *      REACH://<string of MAC addresses>
 *
 * This ensures that the identifier cannot collide with other platforms.
 *
 * A mixin "RandomUuidHardwareIdGeneratorMixin" is provided which implements a
 * random UUID-based algorithm. A mixin "ReachPlayerHardwareIdGeneratorMixin" is
 * provided which can be used on platforms running on REACH C#
 *
 * @returns {Promise.<string, Error>}
 */
PlayerAbstract.prototype._generateAndGetHardwareId = function () {
    throw new Error('concrete player class must implement _generateAndGetHardwareId');
};

/**
 * Configure logging for All platform.
 * @param {Log} log - The log instance to configure.
 * @returns {Promise<void>}
 */
PlayerAbstract.prototype.configureLogging = async function (log) {
    if (this.hasPermission(Permission.FORWARD_PROOF_OF_PLAY)) {
        // Dexie for Proof of Play logging
        log.Mechanism.push(new DexieProofOfPlayLogForwarder());
    }
};

export { PlayerAbstract };
